import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import { useProfile } from "../contexts/ProfileContext";
import { showToast } from "./Toast";
import { useAnalytics } from "../hooks/useAnalytics";
import { getIntroPricing } from "../utils/introOffer";

// The ONE subscribe/trial wall for the members-only web model. Every gated
// action (creation, downloads, voice, star spends) routes here for guests and
// signed-in users without an active entitlement. Entitled users never see it.
//
// Trial and first-month offer terms come from /subscription-status. Stripe
// coupon IDs stay server-side; the browser receives display terms only.
//
// Two-screen flow: the trial timeline plays first (skipped when no trial
// variant is active), then Continue slides into the plans screen.

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const DEFAULT_INTRO_TERMS = {
  pro: {
    enabled: true,
    discount_percent: 45,
    discount_display: "45% off",
    regular_price_display: "$14.99/month",
    regular_annual_price_display: "$143.88/year",
  },
  family: {
    enabled: true,
    discount_percent: 45,
    discount_display: "45% off",
    regular_price_display: "$24.99/month",
    regular_annual_price_display: "$227.88/year",
  },
};

const HEADLINES = {
  creation: "Bring your ideas to life",
  download: "Downloads are for members",
  voice: "Keep the story going",
  stars: "Your stars are waiting",
  launch_explainer: "Pixley is now members-only",
  post_onboarding: "Start creating with Pixley",
  make_onboarding: "Your first episode is ready to make",
  translate: "Read in every language",
  games: "Ready, set, play!",
  api_403: "Membership needed for that",
};

const SUBLINES = {
  creation: "Stories, characters, and storybooks are part of Pixley membership.",
  download: "Members can download and keep everything their family makes.",
  voice: "Voice storybooks are part of Pixley membership.",
  stars: "Start your membership and every star you have unlocks instantly.",
  launch_explainer: "",
  post_onboarding: "Choose a membership and start creating. Cancel anytime.",
  make_onboarding: "Choose a membership and we'll start making it right away. Cancel anytime.",
  translate: "Storybook translations are part of Pixley membership.",
  games: "Pixley games are part of Pixley membership.",
  api_403: "An active Pixley membership is needed for that.",
};

const PLANS = [
  {
    tier: "pro",
    name: "Pro",
    blurb: "Best for most families",
    monthly: 14.99,
    annual: 11.99,
    starsPerWeek: 250,
    badge: "MOST POPULAR",
    features: ["Up to 3 min stories", "9:16 vertical + 16:9", "All languages"],
  },
  {
    tier: "family",
    name: "Family",
    blurb: "For multiple kids",
    monthly: 24.99,
    annual: 18.99,
    starsPerWeek: 650,
    badge: null,
    // Every account gets up to 4 profiles and 3-minute stories on any plan;
    // Family's real extras are the stars and watermark-free downloads
    features: ["Up to 3 min stories", "Watermark-free downloads", "All languages"],
  },
];

const REVIEW = {
  quote: "My 5 year old asks for Pixley every night.",
  author: "Sarah, mom of two",
};

const CONFETTI_COLORS = ["#0065F8", "#FF6B9A", "#FFC93C", "#37D67A", "#8A5CF6", "#00CAFF"];

// ── Small inline icons (SVG only — no emoji per design rules) ──

const UnlockIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);
const BellIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);
const CrownIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 8l4.5 4L12 5l4.5 7L21 8l-1.6 10.2a2 2 0 0 1-2 1.8H6.6a2 2 0 0 1-2-1.8L3 8z" />
  </svg>
);
const GoldStar = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#FFB300">
    <polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 8.9 8.3" />
  </svg>
);
const CheckChip = () => (
  <span
    style={{
      width: 18, height: 18, borderRadius: 999, flex: "0 0 auto",
      background: "#E7F0FF", color: "#0065F8",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}
  >
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12.5l5.5 5.5L20 6.5" />
    </svg>
  </span>
);

// ── Signature press-down button (SubscriptionModal pattern) ──

const popButton = (primary) => ({
  ...FREDOKA,
  width: "100%",
  padding: primary ? "13px 0" : "12px 0",
  borderRadius: 14,
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  color: primary ? "#FFFFFF" : "#0065F8",
  background: primary ? "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)" : "#FFFFFF",
  border: primary ? "1px solid rgba(255,255,255,0.5)" : "2px solid rgba(0,101,248,0.22)",
  boxShadow: primary
    ? "inset 0 1px 0 rgba(255,255,255,0.4), 0px 4px 0px #073B8E"
    : "0px 3px 0px rgba(11,26,51,0.06)",
  transition: "transform 100ms ease, box-shadow 100ms ease",
});

const pressHandlers = (primary) => ({
  onMouseDown: (e) => {
    e.currentTarget.style.transform = "translateY(3px)";
    e.currentTarget.style.boxShadow = "0px 0px 0px transparent";
  },
  onMouseUp: (e) => {
    e.currentTarget.style.transform = "";
    e.currentTarget.style.boxShadow = popButton(primary).boxShadow;
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.transform = "";
    e.currentTarget.style.boxShadow = popButton(primary).boxShadow;
  },
});

// ── Trial transparency timeline ──
// Entrance is staggered: each step slides up, its chip pops with a spring,
// and the rail grows down to hand off to the next step. Keyframes live at
// the modal level (tw-*) so both screens can use them.

export const TrialTimeline = ({ trialConfig }) => {
  const days = trialConfig?.duration_days || 0;
  if (days <= 0) return null;
  const price = trialConfig?.price_display;
  const steps = [
    {
      Icon: UnlockIcon,
      title: "Today",
      body: price
        ? `Pay ${price} and unlock everything — creation, downloads, and your saved stars.`
        : "Start your trial and unlock everything — creation, downloads, and your saved stars.",
      accent: true,
    },
    ...(days > 2
      ? [
          {
            Icon: BellIcon,
            title: `Day ${days - 1}`,
            body: "We'll email you a reminder before your trial ends.",
            accent: false,
          },
        ]
      : []),
    {
      Icon: CrownIcon,
      title: `Day ${days}`,
      body: "Your membership begins. Cancel anytime before this in Settings.",
      accent: false,
    },
  ];
  const STAGGER = 0.42;
  const chip = 46;
  const icon = 21;
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "2px solid rgba(11,26,51,0.06)",
        borderRadius: 20,
        padding: "24px 28px 22px",
        boxShadow: "0px 3px 0px rgba(11,26,51,0.04)",
        margin: "0 0 20px",
      }}
    >
      <div
        style={{
          ...FREDOKA, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em",
          textTransform: "uppercase", color: "#8CA0BC", marginBottom: 14,
        }}
      >
        How your trial works
      </div>
      {steps.map((step, i) => (
        <div
          key={step.title}
          style={{
            display: "flex", gap: 18,
            animation: `tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${(0.15 + i * STAGGER).toFixed(2)}s both`,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              style={{
                width: chip, height: chip, borderRadius: 999, flex: "0 0 auto",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: step.accent
                  ? "linear-gradient(135deg, #0A7DFF 0%, #0065F8 55%, #00CAFF 100%)"
                  : "#E7F0FF",
                color: step.accent ? "#FFFFFF" : "#0065F8",
                boxShadow: step.accent
                  ? "0 6px 14px rgba(0,101,248,0.35), inset 0 1px 0 rgba(255,255,255,0.4)"
                  : "none",
                animation: `tw-chip-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${(0.22 + i * STAGGER).toFixed(2)}s both`,
              }}
            >
              <step.Icon size={icon} />
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  width: 3, flex: 1, minHeight: 22, borderRadius: 2, margin: "2px 0",
                  background: i === 0
                    ? "linear-gradient(180deg, #0065F8 0%, #D8E6FB 100%)"
                    : "#D8E6FB",
                  transformOrigin: "top",
                  animation: `tw-rail-grow 0.35s ease-out ${(0.45 + i * STAGGER).toFixed(2)}s both`,
                }}
              />
            )}
          </div>
          <div style={{ paddingBottom: i < steps.length - 1 ? 20 : 0, paddingTop: 6 }}>
            <div style={{ ...FREDOKA, fontSize: 16.5, fontWeight: 700, color: "#0B1A33" }}>
              {step.title}
            </div>
            <div style={{ fontSize: 14, color: "#5A6B85", lineHeight: 1.5 }}>
              {step.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ReviewCard = () => (
  <div
    style={{
      background: "#FFFFFF",
      border: "2px solid rgba(11,26,51,0.06)", borderRadius: 18,
      boxShadow: "0px 3px 0px rgba(11,26,51,0.04)",
      padding: "10px 16px", display: "flex", alignItems: "center", gap: 12,
    }}
  >
    <div
      style={{
        width: 38, height: 38, borderRadius: "50%", flex: "0 0 auto",
        background: "linear-gradient(135deg, #FF6B9A 0%, #FF8FB3 100%)",
        boxShadow: "0 6px 14px rgba(255,107,154,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        ...FREDOKA, fontSize: 19, fontWeight: 700, color: "#FFFFFF",
      }}
    >
      S
    </div>
    <div style={{ textAlign: "left" }}>
      <div aria-label="5 out of 5 stars" style={{ display: "flex", gap: 2, marginBottom: 3 }}>
        {[0, 1, 2, 3, 4].map((i) => <GoldStar key={i} size={13} />)}
      </div>
      <p style={{ fontSize: 13.5, color: "#0B1A33", fontWeight: 600, margin: "0 0 1px", lineHeight: 1.4 }}>
        “{REVIEW.quote}”
      </p>
      <p style={{ fontSize: 12, color: "#8CA0BC", margin: 0 }}>{REVIEW.author}</p>
    </div>
  </div>
);

const TrialWall = ({ isOpen, onClose, context = {} }) => {
  const { user, isAuthenticated, subscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const { requireParentalVerification } = useProfile();
  const { trackCheckoutStarted } = useAnalytics();
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState("annual");
  const [isProcessing, setIsProcessing] = useState(false);
  const [screen, setScreen] = useState("timeline");
  const introOffer = subscriptionStatus?.introOffer;
  const hasIntroOffer = introOffer?.enabled !== false && introOffer?.eligible !== false;

  useEffect(() => {
    if (isOpen) {
      setScreen("timeline");
      if (hasIntroOffer) setBillingPeriod("monthly");
    }
  }, [isOpen, hasIntroOffer]);

  if (!isOpen) return null;

  const trigger = context.trigger || "creation";
  // make.pixleyai.com buyers (MakeWelcome): name their star, and tie the usual first-period discount to the video
  // they already paid for
  const makeStar = trigger === "make_onboarding" ? (context.star || "").trim() : "";
  const makeOffer = introOffer?.make_buyer === true && introOffer?.eligible !== false;
  // took the iPhone "free with a trial" offer but signed in on the web: the held video comes with the membership
  const offerVideo = makeStar && context.offerVideo ? String(context.offerVideo) : "";
  const headline = offerVideo
    ? `Get ${makeStar}'s video free`
    : makeStar
    ? `${makeStar}'s first episode is ready to make`
    : HEADLINES[trigger] || HEADLINES.creation;
  // make buyers get a free trial instead of the first-period discount (backend trial_config, 2026-10-14)
  const makeTrialDays = makeOffer ? (subscriptionStatus?.trialConfig?.duration_days || 0) : 0;
  const subline = offerVideo
    ? `Become a member and we'll make ${makeStar}'s ${offerVideo} video right away, free. Cancel anytime.`
    : makeStar && makeTrialDays > 0
    ? `You already made ${makeStar}'s video with us, so your first ${makeTrialDays} days are free. Cancel anytime.`
    : makeStar && makeOffer
    ? `You already made ${makeStar}'s video with us, so your first ${billingPeriod === "annual" ? "year" : "month"} is ${introOffer?.plans?.pro?.discount_percent || 45}% off. Cancel anytime.`
    : SUBLINES[trigger] ?? SUBLINES.creation;
  // iOS-style mandatory paywall: only the wall embedded IN the onboarding
  // flow (step 8) is unclosable — every other appearance, including the
  // post-onboarding reload fallback, is dismissible.
  const mandatory = context.mandatory === true;
  const handleDismiss = mandatory ? undefined : onClose;
  const trialConfig = subscriptionStatus?.trialConfig;
  const hasTrial = (trialConfig?.duration_days || 0) > 0;
  const trialCta = hasTrial
    ? trialConfig.price_display
      ? `Start ${trialConfig.price_display} trial`
      : `Start free trial`
    : "Become a member";
  const planIntroOffer = (tier) => introOffer?.plans?.[tier] || DEFAULT_INTRO_TERMS[tier];
  const showsIntroOffer = (tier) => (
    hasIntroOffer
    && planIntroOffer(tier)?.enabled === true
  );
  const introPricingFor = (plan) => getIntroPricing({
    monthlyPrice: plan.monthly,
    annualMonthlyPrice: plan.annual,
    billingPeriod,
    discountPercent: planIntroOffer(plan.tier)?.discount_percent || 45,
  });
  const planCta = (plan) => showsIntroOffer(plan.tier)
    ? `Start ${plan.name} — ${introPricingFor(plan).discountedPrice}`
    : trialCta;

  const confetti = Array.from({ length: 14 }, (_, i) => ({
    left: `${(i * 41 + 7) % 100}%`,
    delay: `${(i % 7) * 0.5}s`,
    duration: `${3.2 + (i % 5) * 0.5}s`,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 6 + (i % 3) * 3,
    rotate: (i * 53) % 360,
  }));

  const startCheckout = (tier) => {
    const execute = async () => {
      if (!user?.id || !user?.email) {
        showToast("Unable to process. Try logging out and back in.", "error");
        return;
      }
      if (isProcessing) return;
      try {
        setIsProcessing(true);
        trackCheckoutStarted("trial_wall", tier);
        const r = await apiClient.createStripeCheckoutSession(
          user.id,
          user.email,
          tier === "pro" ? "pro" : "family",
          billingPeriod,
          window.location.pathname + window.location.search,
        );
        if (r.status && r.data.checkout_url) {
          window.location.href = r.data.checkout_url;
        } else {
          throw new Error(r.error);
        }
      } catch (e) {
        showToast("Failed to start checkout. Please try again.", "error");
        setIsProcessing(false);
      }
    };
    requireParentalVerification("start Pixley membership", execute);
  };

  const billingToggle = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 24 }}>
      <span style={{ ...FREDOKA, fontSize: 14, fontWeight: 700, color: billingPeriod === "monthly" ? "#0B1A33" : "#8CA0BC" }}>
        Monthly
      </span>
      <button
        onClick={() => setBillingPeriod(billingPeriod === "annual" ? "monthly" : "annual")}
        aria-label="Toggle billing period"
        style={{
          position: "relative", width: 56, height: 30, borderRadius: 999,
          background: billingPeriod === "annual" ? "#0065F8" : "#D7E3F3",
          border: "none", cursor: "pointer", transition: "background 200ms ease",
          boxShadow: "inset 0 2px 4px rgba(11,26,51,0.1)",
        }}
      >
        <div
          style={{
            position: "absolute", top: 3, left: billingPeriod === "annual" ? 29 : 3,
            width: 24, height: 24, borderRadius: "50%", background: "#FFF",
            boxShadow: "0px 2px 4px rgba(11,26,51,0.2)", transition: "left 200ms ease",
          }}
        />
      </button>
      <span style={{ ...FREDOKA, fontSize: 14, fontWeight: 700, color: billingPeriod === "annual" ? "#0B1A33" : "#8CA0BC" }}>
        Annual
      </span>
      <span
        style={{
          padding: "4px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 800,
          background: "#0065F8", color: "#FFF", boxShadow: "0px 2px 0px #073B8E",
          letterSpacing: "0.04em",
        }}
      >
        SAVE 25%
      </span>
    </div>
  );

  const planGrid = (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 16 }}>
      {PLANS.map((plan, idx) => (
        <div
          key={plan.tier}
          style={{
            position: "relative", display: "flex", flexDirection: "column",
            animation: `tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${(0.2 + idx * 0.18).toFixed(2)}s both`,
          }}
        >
          {plan.badge && (
            <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
              <span
                style={{
                  padding: "5px 14px", borderRadius: 999, fontSize: 10, fontWeight: 800,
                  letterSpacing: "0.08em", textTransform: "uppercase", color: "#FFF",
                  background: "#0065F8", boxShadow: "0px 3px 0px #073B8E", whiteSpace: "nowrap",
                }}
              >
                {plan.badge}
              </span>
            </div>
          )}
          <div
            style={{
              flex: 1, borderRadius: 20, padding: "28px 20px 22px",
              background: "#FFFFFF",
              border: plan.badge ? "2.5px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
              boxShadow: plan.badge
                ? "0px 4px 0px rgba(0,101,248,0.15), 0px 12px 32px rgba(0,101,248,0.10)"
                : "0px 2px 0px rgba(11,26,51,0.04)",
              display: "flex", flexDirection: "column",
            }}
          >
            <h3
              style={{
                ...FREDOKA, fontSize: 20, fontWeight: 700, color: "#0065F8",
                textAlign: "center", margin: 0, textTransform: "uppercase",
              }}
            >
              {plan.name}
            </h3>
            <p style={{ fontSize: 11.5, color: "#8CA0BC", textAlign: "center", margin: "2px 0 8px" }}>
              {plan.blurb}
            </p>
            <div style={{ textAlign: "center", marginBottom: 4 }}>
              {showsIntroOffer(plan.tier) ? (() => {
                const pricing = introPricingFor(plan);
                return (
                  <>
                    <div style={{ display: "inline-flex", alignItems: "center", padding: "6px 14px", borderRadius: 999, background: "#E8F2FF", color: "#0065F8", ...FREDOKA, fontSize: 15, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 11 }}>
                      {pricing.discountPercent}% OFF
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: billingPeriod === 'annual' ? 9 : 13, flexWrap: "nowrap", whiteSpace: "nowrap", width: "100%" }}>
                      <span style={{ ...FREDOKA, fontSize: billingPeriod === 'annual' ? 29 : 34, fontWeight: 700, color: "#8A99B3", lineHeight: 1, letterSpacing: "-0.02em", textDecoration: "line-through", textDecorationThickness: 3 }}>
                        {pricing.regularPrice}
                      </span>
                      <span style={{ ...FREDOKA, fontSize: billingPeriod === 'annual' ? 29 : 34, fontWeight: 700, color: "#0B1A33", lineHeight: 1, letterSpacing: "-0.02em" }}>
                        {pricing.discountedPrice}
                      </span>
                    </div>
                    <div style={{ ...FREDOKA, fontSize: 13, fontWeight: 600, color: "#334155", marginTop: 4 }}>
                      for your {pricing.periodLabel}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#5A6B85", marginTop: 4 }}>
                      Then {pricing.renewalPrice} · cancel anytime
                    </div>
                  </>
                );
              })() : (
                <>
                  <span style={{ ...FREDOKA, fontSize: 36, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>
                    ${billingPeriod === "annual" ? plan.annual : plan.monthly}
                  </span>
                  <span style={{ fontSize: 12.5, color: "#8A99B3" }}>/mo</span>
                </>
              )}
            </div>
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <span style={{ ...FREDOKA, fontSize: 15, fontWeight: 700, color: "#0065F8" }}>
                {plan.starsPerWeek}
              </span>
              <span style={{ fontSize: 11.5, color: "#334155" }}> stars every week</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px", flex: 1 }}>
              {plan.features.map((f) => (
                <li key={f} style={{ fontSize: 13, color: "#334155", padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckChip /> {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => startCheckout(plan.tier)}
              disabled={isProcessing}
              style={{
                ...popButton(!!plan.badge),
                fontSize: 14,
                cursor: isProcessing ? "wait" : "pointer",
                opacity: isProcessing ? 0.7 : 1,
              }}
              {...pressHandlers(!!plan.badge)}
            >
              {planCta(plan)}
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const footnote = (
    <p style={{ fontSize: 11.5, color: "#8CA0BC", textAlign: "center", margin: "20px 20px 0" }}>
      {hasIntroOffer
        ? `The introductory price applies to your ${billingPeriod === "annual" ? "first year" : "first month"}, then renews at the regular price. Cancel anytime in Settings.`
        : hasTrial
        ? `After your ${trialConfig.duration_days}-day trial, your plan renews automatically. Cancel anytime in Settings.`
        : "Renews automatically. Cancel anytime in Settings."}
    </p>
  );

  // Skip the timeline screen entirely when there's no trial to explain
  const showTimeline = hasTrial && screen === "timeline";

  const modal = (
    <div
      onClick={handleDismiss}
      className="animate-fade-in"
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(11,26,51,0.5)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, overflowY: "auto",
      }}
    >
      <style>{`
        @keyframes trialwall-confetti-fall {
          0% { top: -6%; transform: rotate(0deg); opacity: 1; }
          80% { opacity: 1; }
          100% { top: 104%; transform: rotate(420deg); opacity: 0; }
        }
        @keyframes tw-step-in {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes tw-chip-pop {
          0% { transform: scale(0.3); }
          70% { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        @keyframes tw-rail-grow {
          0% { transform: scaleY(0); }
          100% { transform: scaleY(1); }
        }
        @keyframes tw-screen-in {
          0% { opacity: 0; transform: translateX(36px); }
          100% { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-slide-up"
        style={{
          width: "100%", maxWidth: isAuthenticated ? (showTimeline ? 560 : 700) : 560,
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
          borderRadius: 28,
          boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
          position: "relative",
          fontFamily: "'Inter', 'Fredoka', sans-serif",
          minHeight: isAuthenticated ? "min(780px, 90vh)" : undefined,
          maxHeight: "94vh", overflowY: "auto", overflowX: "hidden",
          display: "flex", flexDirection: "column",
          transition: "max-width 300ms ease",
        }}
      >
        {/* ── Hero band: warm radial + confetti + Pip ── */}
        <div
          style={{
            position: "relative", overflow: "hidden", flexShrink: 0,   // never squeezed on a short phone screen
            background: "radial-gradient(130% 100% at 50% 0%, #DCEBFF 0%, #F5FAFF 55%, rgba(245,250,255,0) 100%)",
            padding: "34px 28px 10px",
            textAlign: "center",
          }}
        >
          {confetti.map((c, i) => (
            <span
              key={i}
              aria-hidden="true"
              style={{
                position: "absolute", top: "-6%", left: c.left,
                width: c.size, height: c.size * 0.45,
                background: c.color, borderRadius: 2,
                transform: `rotate(${c.rotate}deg)`,
                animation: `trialwall-confetti-fall ${c.duration} linear ${c.delay} infinite`,
                pointerEvents: "none", opacity: 0.85,
              }}
            />
          ))}

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, position: "relative" }}>
            <img
              src="/assets/pixley-logo-full.png"
              alt="Pixley"
              style={{ height: 96, objectFit: "contain" }}
            />
          </div>

          <h2
            style={{
              ...FREDOKA, fontSize: "clamp(24px, 3.2vw, 29px)", fontWeight: 700,
              color: "#0B1A33", margin: "0 0 5px", lineHeight: 1.15,
              position: "relative",
            }}
          >
            {headline}
          </h2>
          {subline && (
            <p style={{ fontSize: 13.5, color: "#5A6B85", margin: "0 auto 10px", maxWidth: 460, lineHeight: 1.5, position: "relative" }}>
              {subline}
            </p>
          )}
        </div>

        {/* Back to the trial timeline — mirrors the close button, top-left */}
        {isAuthenticated && hasTrial && !showTimeline && (
          <button
            onClick={() => setScreen("timeline")}
            aria-label="Back"
            style={{
              position: "absolute", top: 16, left: 16, zIndex: 10,
              width: 40, height: 40, borderRadius: 14,
              background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)",
              cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#334155",
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Close button (SubscriptionModal pattern); hidden on the mandatory wall */}
        {!mandatory && (
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: "absolute", top: 16, right: 16, zIndex: 10,
              width: 40, height: 40, borderRadius: 14,
              background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)",
              cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#334155",
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div style={{ padding: "14px 32px 34px", flex: 1, display: "flex", flexDirection: "column" }}>
          {!isAuthenticated ? (
            <div style={{ maxWidth: 380, margin: "0 auto", padding: "4px 0 6px" }}>
              <button
                onClick={() => {
                  onClose?.();
                  navigate("/auth?mode=signup&redirect=" +
                    encodeURIComponent(window.location.pathname + window.location.search));
                }}
                style={{ ...popButton(true), fontSize: 16, padding: "15px 0", marginBottom: 10 }}
                {...pressHandlers(true)}
              >
                Get started
              </button>
              <p style={{ fontSize: 12.5, color: "#8CA0BC", textAlign: "center", margin: "0 0 16px" }}>
                Create an account, then start your membership.
              </p>
              <ReviewCard />
            </div>
          ) : showTimeline ? (
            /* ── Screen 1: the trial story, full width ── */
            <div key="timeline" style={{ animation: "tw-screen-in 0.4s ease-out both", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
              <TrialTimeline trialConfig={trialConfig} large />
              <div style={{ animation: "tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.5s both", marginBottom: 18 }}>
                <ReviewCard />
              </div>
              <div
                style={{
                  // width:100% needed — auto side-margins stop flex stretch,
                  // which otherwise shrinks this to the button's text width
                  width: "100%", maxWidth: 430, margin: "0 auto",
                  animation: `tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.7s both`,
                }}
              >
                <button
                  onClick={() => setScreen("plans")}
                  style={{ ...popButton(true), fontSize: 16, padding: "14px 0" }}
                  {...pressHandlers(true)}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            /* ── Screen 2: the paywall ── */
            <div key="plans" style={{ animation: "tw-screen-in 0.4s ease-out both", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
              <div style={{ animation: "tw-step-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.05s both" }}>
                {billingToggle}
              </div>
              {planGrid}
              <div style={{ animation: "tw-step-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both" }}>
                {footnote}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default TrialWall;
